import { Device } from "../models";
import { useEffect, useRef } from "react";

type WebRtcPlayerProps = {
    device: Device;
    /** Classes for the underlying video element; defaults to the dashboard's centred, full-width layout. */
    className?: string;
};

const DEFAULT_TRACKER = "wss://rtc.omnisightusa.com:8000/";

function decode(buffer: ArrayBuffer | number[]) {
    return String.fromCharCode(...new Uint8Array(buffer));
}

function encode(string: string) {
    return Uint8Array.from(string, (c) => c.charCodeAt(0));
}

async function cipher(share: string, pwd: string) {
    const hash = await crypto.subtle.digest("SHA-256", encode(share));
    const nonce = (Date.now() * 1000000).toString(36);

    const ivData = await crypto.subtle.digest("SHA-256", encode(share + ":" + nonce));
    const keyData = await crypto.subtle.digest("SHA-256", encode(nonce + ":" + pwd));
    const key = await crypto.subtle.importKey("raw", keyData, { name: "AES-GCM" }, false, ["encrypt", "decrypt"]);

    return {
        hash: btoa(decode(hash)),
        nonce: nonce,
        encrypt: async function (plaintext: string) {
            const cryptotext = await crypto.subtle.encrypt(
                { name: "AES-GCM", iv: ivData.slice(0, 12), additionalData: encode(nonce) },
                key,
                encode(plaintext),
            );
            return btoa(decode(cryptotext));
        },
        decrypt: async function (cryptotext: string) {
            const plaintext = await crypto.subtle.decrypt(
                { name: "AES-GCM", iv: ivData.slice(0, 12), additionalData: encode(nonce) },
                key,
                encode(atob(cryptotext)),
            );
            return decode(plaintext);
        },
    };
}

/** Resolves with the local SDP once ICE gathering completes, or after `timeout` ms with whatever has been gathered. */
function getOffer(pc: RTCPeerConnection, timeout = 5000) {
    return new Promise<string>((resolve) => {
        pc.addEventListener("icegatheringstatechange", () => {
            if (pc.iceGatheringState === "complete") resolve(pc.localDescription?.sdp ?? "");
        });

        pc.createOffer()
            .then((offer) => pc.setLocalDescription(offer))
            .catch(() => resolve(""));

        setTimeout(() => resolve(pc.localDescription?.sdp ?? ""), timeout);
    });
}

/**
 * Plays a unit's live WebRTC feed. The device id and key are the share/password pair the unit
 * announces itself with on the rtc tracker, so this component is self-sufficient once the caller
 * has asked the server to queue a StreamVideo start command (`Devices.getDeviceStream`).
 *
 * The peer connection is created synchronously when the effect runs and closed by its cleanup, so
 * unmounting — or a Stop pressed while the offer is still being negotiated — always ends the stream.
 * Anything the async negotiation produces after cleanup (the tracker socket, the answer) is dropped.
 */
export const WebRtcPlayer: React.FC<WebRtcPlayerProps> = ({ device, className = "w-full mx-auto my-auto" }) => {
    const videoPlayer = useRef<HTMLVideoElement>(null);
    const share = device?.id ?? "";
    const pwd = device?.deviceKey ?? "";

    useEffect(() => {
        let cancelled = false;
        let ws: WebSocket | undefined;
        const video = videoPlayer.current;

        const pc = new RTCPeerConnection({
            iceServers: [{ urls: "stun:stun.l.google.com:19302" }],
        });
        const tracks = ["video", "audio"].map((kind) => pc.addTransceiver(kind, { direction: "recvonly" }).receiver.track);
        if (video) video.srcObject = new MediaStream(tracks);

        (async () => {
            const crypto = await cipher(share, pwd);
            const offer = await crypto.encrypt(await getOffer(pc));
            if (cancelled) return;

            ws = new WebSocket(DEFAULT_TRACKER);
            ws.addEventListener("open", () => {
                ws?.send(
                    JSON.stringify({
                        action: "announce",
                        info_hash: crypto.hash,
                        peer_id: Math.random().toString(36).substring(2),
                        offers: [
                            {
                                offer_id: crypto.nonce,
                                offer: { type: "offer", sdp: offer },
                            },
                        ],
                        numwant: 1,
                    }),
                );
            });

            ws.addEventListener("message", async (ev) => {
                const msg = JSON.parse(ev.data);
                if (!msg.answer) return;

                const answer = await crypto.decrypt(msg.answer.sdp);
                if (cancelled) return;
                await pc.setRemoteDescription({ type: "answer", sdp: answer });

                ws?.close();
            });
        })().catch((e) => {
            if (!cancelled) console.error("WebRTC negotiation failed", e);
        });

        return () => {
            cancelled = true;
            ws?.close();
            pc.close();
            if (video) video.srcObject = null;
        };
    }, [share, pwd]);

    return <video ref={videoPlayer} autoPlay controls playsInline muted className={className} />;
};
